import React from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "@learningblocks/shared-components/lib/FormattedMessage";
import { sanitizeConfigOptions } from "@labxchange/util-sanitization";

import { default as i18n } from "@learningblocks/shared-components/lib/i18n";
import { Modal } from "@labxchange/ui-components/lib/Modal";
import { SingleSelectQuestion } from "@labxchange/ui-components/lib/SingleSelectQuestion";
import { MultiSelectQuestion } from "@labxchange/ui-components/lib/MultiSelectQuestion";
import { TableQuestion } from "@labxchange/ui-components/lib/TableQuestion";

import { LearnMore } from "@learningblocks/interactives/interactive-infographic-components/lib/LearnMore";
import { SliderWidget } from "@learningblocks/interactives/interactive-infographic-components/lib/SliderWidget";

import className from "../style.module.scss";

export function WidgetWrapper(props) {
  const widgetData = { ...props.widgetProps };
  const totalAttempts =
    widgetData?.totalAttempts < 0 ? -1 : widgetData?.totalAttempts;
  // CONTENT_BASE_URL may be undefined on some environments (e.g. stage servers).
  // Fall back to location.pathname so image URLs are resolved relative to the
  // simulation's own path rather than producing a literal "undefined" prefix.
  const baseUrl = window.CONTENT_BASE_URL ?? location.pathname;

  const imageObj = widgetData.image
    ? {
        src: props.preview
          ? widgetData.image.url
          : `${baseUrl}locales/${i18n.language}/images/${widgetData.image.src}`,
        altText: widgetData.image.altText
          ? widgetData.image.altText
          : "Question illustration",
        containerBgColor: "#003e6b",
        ...(widgetData.image.caption && {
          caption: widgetData.image.caption,
        }),
      }
    : undefined;

  const closeWidget = () => {
    if (props.type === "learn-more" || props.type === "Slider") {
      props.handleTrigger(props.hotspotID);
    } else if (props.widgetProps.complete) {
      props.handleTrigger(props.hotspotID);
    }
    props.handleCloseWidget();
  };

  return (
    <Modal onClose={closeWidget}>
      <div className={className.widget}>
        {props.type === "text-body" ? (
          <div className={className.textBodyWidget}>
            <FormattedMessage
              string={props.widgetProps.text}
              sanitizeConfig={sanitizeConfigOptions.BlockText}
              description=""
            />
          </div>
        ) : props.type === "CSCQ" ? (
          <SingleSelectQuestion
            {...widgetData}
            id={props.hotspotID}
            totalAttempts={totalAttempts}
            setCompleted={(completed) => {
              if (completed) {
                props.handleWidgetComplete(props.hotspotID);
              }
            }}
            onReset={() => props.handleWidgetReset(props.hotspotID)}
            image={imageObj}
          />
        ) : props.type === "MSCQ" ? (
          <MultiSelectQuestion
            {...props.widgetProps}
            id={props.hotspotID}
            content={[...widgetData.content, "options"]}
            options={widgetData.checkboxes || []}
            totalAttempts={totalAttempts}
            setCompleted={(completed) => {
              if (completed) {
                props.handleWidgetComplete(props.hotspotID);
              }
            }}
            onReset={() => props.handleWidgetReset(props.hotspotID)}
            image={imageObj}
          />
        ) : props.type === "learn-more" ? (
          <LearnMore {...props.widgetProps} preview={props.preview} />
        ) : props.type === "TABLE_QUESTION" ? (
          <TableQuestion
            {...props.widgetProps}
            key={props.hotspotID}
            data={(widgetData.data || []).map((row) =>
              row.map((cell) =>
                cell.type === "INPUT" || cell.type === "DROPDOWN"
                  ? { ...cell, showFeedback: true }
                  : cell
              )
            )}
            title={widgetData.heading || widgetData.title}
            hint={widgetData.hintDetails || widgetData.hint}
            totalAttempts={totalAttempts}
            correct={widgetData.complete}
            submitDisabled={widgetData.complete}
            questionType={widgetData.type}
            content={
              // The legacy simData `content` field was designed for an older component
              // that always rendered the table. The new TableQuestion only renders the
              // table when "table" is present in `content`. Adding this to ensure it
              // is always included.
              (widgetData.content
                ? widgetData.content.includes("table")
                  ? widgetData.content
                  : [...widgetData.content, "table"]
                : ["text", "table"]
              ).map((item) =>
                item === "heading"
                  ? "title"
                  : item === "hintDetails"
                    ? "hint"
                    : item
              )
            }
            setCompleted={(completed) => {
              if (completed) {
                props.handleWidgetComplete(props.hotspotID);
              }
            }}
            onReset={() => props.handleWidgetReset(props.hotspotID)}
            image={imageObj}
          />
        ) : props.type === "Slider" ? (
          <SliderWidget data={props.widgetProps} />
        ) : null}
      </div>
    </Modal>
  );
}

WidgetWrapper.propTypes = {
  closeWidget: PropTypes.bool.isRequired,
  type: PropTypes.string.isRequired,
  widgetProps: PropTypes.object.isRequired,
  handleCloseWidget: PropTypes.func.isRequired,
  preview: PropTypes.bool,
  handleWidgetComplete: PropTypes.func.isRequired,
  handleWidgetReset: PropTypes.func.isRequired,
};
WidgetWrapper.defaultProps = {
  preview: false,
};
